import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Chat from './components/Chat';
import EnhancedAdminPanel from './components/EnhancedAdminPanel';
import AgentPanel from './components/agents/AgentPanel';
import MediaUpload from './components/media/MediaUpload';
import MediaGallery from './components/media/MediaGallery';
import MemorySearch from './components/memory/MemorySearch';
import Navbar from './components/Navbar';
import ImageEffectsStudio from './components/media/ImageEffectsStudio';

function ProtectedRoute({ children, adminOnly = false, agentsRequired = false, uploadRequired = false }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-primary">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-4"></div>
          <div className="text-xl font-semibold text-gray-700 dark:text-dark-primary">Loading Jarvis AI...</div>
          <div className="text-sm text-gray-500 dark:text-dark-secondary mt-2">Phase 5 Enhanced Dashboard</div>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" />;
  }
  
  if (agentsRequired && !user.can_use_agents) {
    return <Navigate to="/dashboard" />;
  }

  if (uploadRequired && !user.can_upload) {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
}

function AppContent() {
  const { user } = useAuth();
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-primary transition-colors duration-200">
      {user && <Navbar />}
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />
        
        {/* Enhanced Dashboard with Tabs */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        
        {/* Traditional Chat Interface */}
        <Route path="/chat" element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        } />
        
        {/* Agent Management */}
        <Route path="/agents" element={
          <ProtectedRoute agentsRequired={true}>
            <AgentPanel />
          </ProtectedRoute>
        } />
        
        {/* Media Upload */}
        <Route path="/upload" element={
          <ProtectedRoute uploadRequired={true}>
            <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
              <div className="px-4 py-6 sm:px-0">
                <div className="mb-8">
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-primary">Media Upload</h1>
                  <p className="mt-2 text-lg text-gray-600 dark:text-dark-secondary">
                    Upload and process images, videos, audio files, and documents
                  </p>
                </div>
                <MediaUpload onUploadComplete={(result) => console.log('Upload completed:', result)} />
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        {/* Media Gallery */}
        <Route path="/gallery" element={
          <ProtectedRoute uploadRequired={true}>
            <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
              <div className="px-4 py-6 sm:px-0">
                <div className="mb-8">
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-primary">Media Gallery</h1>
                  <p className="mt-2 text-lg text-gray-600 dark:text-dark-secondary">
                    Browse and manage your uploaded media
                  </p>
                </div>
                <MediaGallery />
              </div>
            </div>
          </ProtectedRoute>
        } />

        {/* ImageEffectsStudio */}
        <Route path="/effects" element={
          <ProtectedRoute uploadRequired={true}>
            <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
              <div className="px-4 py-6 sm:px-0">
                <div className="mb-8">
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-primary">Image Effects Studio</h1>
                  <p className="mt-2 text-lg text-gray-600 dark:text-dark-secondary">
                    Upload images and apply the required effects
                  </p>
                </div>
                <ImageEffectsStudio />
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        {/* Memory Search */}
        <Route path="/memory" element={
          <ProtectedRoute uploadRequired={true}>
            <div className="py-6 sm:px-6 lg:px-8">
              <div className="px-4 py-6 sm:px-0">
                <MemorySearch />
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        {/* Enhanced Admin Panel */}
        <Route path="/admin" element={
          <ProtectedRoute adminOnly={true}>
            <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
              <div className="px-4 py-6 sm:px-0">
                <EnhancedAdminPanel />
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        {/* Default redirect */}
        <Route path="/" element={<Navigate to={user ? "/dashboard" : "/login"} />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;